import { Component, Inject, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxExtendedPdfViewerComponent } from 'ngx-extended-pdf-viewer';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';

export type SecureDocMediaType = 'pdf' | 'video' | 'image' | 'audio' | 'external';

export interface SecureDocViewerData {
  docUrl: string;
  pageNumber?: number;
  title?: string;
  imageDescription?: string;
  isExternalPage?: boolean;
  mediaType?: SecureDocMediaType;
  enableImageCopyActions?: boolean;
  enableImageZoomActions?: boolean;
  /** Video playback window, in seconds. */
  startTime?: number;
  endTime?: number;
  /** JHUD-5267: source document an image was extracted from. */
  referenceFileName?: string;
  referencePageNumber?: number;
  /** Invoked with no page for the file link, or with the page number for the page link. */
  onOpenReference?: (pageNumber?: number) => void;
}

@Component({
    selector: 'app-secure-doc-viewer-dialog',
    templateUrl: './secure-doc-viewer-dialog.component.html',
    styleUrl: './secure-doc-viewer-dialog.component.scss',
    standalone: false
})


export class SecureDocViewerDialogComponent implements OnInit, OnDestroy {
  docUrl: string;
  pdfViewerUrl: string;
  externalViewerUrl: SafeResourceUrl | null = null;
  mediaUrl: SafeUrl | null = null;
  mediaType: SecureDocMediaType = 'pdf';
  startTime?: number;
  endTime?: number;
  currentPage: number = 1;
  title: string = 'File Viewer';
  documentFileName: string = '';
  imageDescription: string = '';
  isLoading: boolean = true;
  isMaximized: boolean = false;
  isExternalPage: boolean = false;
  enableImageCopyActions: boolean = false;
  enableImageZoomActions: boolean = false;
  referenceFileName: string = '';
  referencePageNumber?: number;
  imageZoomScale: number = 1;
  readonly minImageZoomScale: number = 0.5;
  readonly maxImageZoomScale: number = 3;
  readonly imageZoomStep: number = 0.25;

  @ViewChild('pdfViewer', { static: false }) pdfViewer: NgxExtendedPdfViewerComponent;
  isShowpad: boolean = false;
  showpad_doc_url: boolean = false;
  URL = 'https://nam04.safelinks.protection.outlook.com/?url=https%3A%2F%2Fbit.ly%2FShowpadDigitalRequestForm&data=05%7C02%7CDChow1%40ITS.JNJ.com%7C1f9e488ba7fb42887b4208deee4ec4e7%7C3ac94b33913548219502eafda6592a35%7C0%7C0%7C639210219230701684%7CUnknown%7CTWFpbGZsb3d8eyJFbXB0eU1hcGkiOnRydWUsIlYiOiIwLjAuMDAwMCIsIlAiOiJXaW4zMiIsIkFOIjoiTWFpbCIsIldUIjoyfQ%3D%3D%7C0%7C%7C%7C&sdata=fXc31a9HtFyK7Nxjr%2BCspz4bXNlWILBDdh3s7UZwnI0%3D&reserved=0';
  roleName: boolean = false;
  constructor(
    public dialogRef: MatDialogRef<SecureDocViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SecureDocViewerData,
    private readonly toastr: ToastrService,
    private readonly el: ElementRef,
    private readonly sanitizer: DomSanitizer
  ) {
    this.docUrl = data.docUrl;
    this.mediaType = data.mediaType || (data.isExternalPage ? 'external' : 'pdf');
    this.isExternalPage = this.mediaType === 'external';
    this.pdfViewerUrl = this.docUrl;
    this.externalViewerUrl = this.isExternalPage
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.docUrl)
      : null;
    this.mediaUrl = (this.mediaType === 'video' || this.mediaType === 'image' || this.mediaType === 'audio')
      ? this.sanitizer.bypassSecurityTrustUrl(this.docUrl)
      : null;
    this.startTime = data.startTime;
    this.endTime = data.endTime;
    this.currentPage = data.pageNumber || 1;
    this.imageDescription = this.mediaType === 'image' ? String(data.imageDescription || '').trim() : '';
    this.enableImageCopyActions = this.mediaType === 'image' && data.enableImageCopyActions === true;
    this.enableImageZoomActions = this.mediaType === 'image' && data.enableImageZoomActions === true;
    this.referenceFileName = String(data.referenceFileName || '').trim();
    this.referencePageNumber = data.referencePageNumber;
    // Files are opened from blob object URLs, so the name only ever comes from data.title.
    const fileNameFromTitle = data.title ? this.getFileName(data.title) : '';
    this.documentFileName = this.isPlaceholderTitle(fileNameFromTitle) ? '' : fileNameFromTitle;
  }

  get hasReferenceDocument(): boolean {
    return !!this.referenceFileName && typeof this.data?.onOpenReference === 'function';
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Opens the source reference document of the displayed image, at the
  //   first page for the file link and at the given page for the page link.
  //
  // MODIFICATION HISTORY (latest first - keep ONLY the last 3):
  //   [JHUD-5267] 2026-09-30 11:20 | Venkatesan | Initial implementation.
  // -----------------------------------------------------------------
  /**
   * Delegates reference-document opening back to the host component.
   * @param pageNumber Page to open; omitted to open at the first page.
   * @param event Optional click event to suppress default anchor navigation.
   */
  openReferenceDocument(pageNumber?: number, event?: MouseEvent): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!this.hasReferenceDocument) {
      return;
    }
    this.data.onOpenReference(pageNumber);
  }

  ngOnInit(): void {
    // Disable right-click on the dialog to prevent "Save as" context menu
    this.el.nativeElement.addEventListener('contextmenu', this.disableRightClick);

    // Block Ctrl+S, Ctrl+P, Ctrl+Shift+S keyboard shortcuts
    document.addEventListener('keydown', this.blockShortcuts);
    this.isShowpad = sessionStorage.getItem('is_showpad_user') === 'true' || false;
    this.showpad_doc_url = sessionStorage.getItem('showpad_doc_url') === 'true' || false;
    const roleName = sessionStorage.getItem("role");
    if(roleName === 'SUPERVISOR'){
      this.roleName = true;
    }
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('contextmenu', this.disableRightClick);
    document.removeEventListener('keydown', this.blockShortcuts);
  }

  disableRightClick = (event: MouseEvent): void => {
    event.preventDefault();
  };

  blockShortcuts = (event: KeyboardEvent): void => {
    // Block Ctrl+S (Save), Ctrl+P (Print), Ctrl+Shift+S (Save As), Ctrl+C (Copy)
    if (event.ctrlKey || event.metaKey) {
      if (
        event.key === 's' || event.key === 'S' ||
        event.key === 'p' || event.key === 'P' ||
        ((!this.enableImageCopyActions) && (event.key === 'c' || event.key === 'C'))
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  async copyImageToClipboard(): Promise<void> {
    if (!this.enableImageCopyActions || this.mediaType !== 'image' || !this.docUrl) {
      return;
    }

    if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
      this.toastr.error('Image copy is not supported in this browser.', '', {
        positionClass: 'toast-top-right'
      });
      return;
    }

    try {
      const response = await fetch(this.docUrl, { credentials: 'omit' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type || 'image/png']: blob })
      ]);
      this.toastr.success('Image copied to clipboard!', '', {
        positionClass: 'toast-top-right'
      });
    } catch {
      this.toastr.error('Unable to copy image. Please try again.', '', {
        positionClass: 'toast-top-right'
      });
    }
  }

  zoomInImage(): void {
    if (!this.enableImageZoomActions || this.mediaType !== 'image') {
      return;
    }
    this.imageZoomScale = Math.min(this.imageZoomScale + this.imageZoomStep, this.maxImageZoomScale);
  }

  zoomOutImage(): void {
    if (!this.enableImageZoomActions || this.mediaType !== 'image') {
      return;
    }
    this.imageZoomScale = Math.max(this.imageZoomScale - this.imageZoomStep, this.minImageZoomScale);
  }

  resetImageZoom(): void {
    this.imageZoomScale = 1;
  }

  onPdfLoaded(): void {
    this.isLoading = false;
  }

  onMediaLoaded(): void {
    this.isLoading = false;
  }

  onMediaLoadFailed(): void {
    this.isLoading = false;
    this.toastr.error('An error occurred while loading the file. Please try again.', '', {
      positionClass: 'toast-top-right'
    });
  }

  get mediaPlayerType(): 'audio' | 'video' {
    return this.mediaType === 'audio' ? 'audio' : 'video';
  }

  onExternalPageLoaded(): void {
    this.isLoading = false;
  }

  onExternalPageLoadFailed(): void {
    this.isLoading = false;
    this.toastr.error('Unable to load Showpad content in the viewer. Please try again.', '', {
      positionClass: 'toast-top-right'
    });
  }

  onPdfLoadFailed(): void {
    this.isLoading = false;
    this.toastr.error('An error occurred while loading the document. Please try again.', '', {
      positionClass: 'toast-top-right'
    });
  }

  toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.updatePosition({ top: '0', left: '0' });
    } else {
      this.dialogRef.updateSize('90vw', '90vh');
      this.dialogRef.updatePosition();
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private getFileName(value: string): string {
    const valueWithoutQuery = value.split(/[?#]/, 1)[0];
    const fileName = valueWithoutQuery.substring(valueWithoutQuery.lastIndexOf('/') + 1);

    try {
      return decodeURIComponent(fileName) || 'Document Viewer';
    } catch {
      return fileName || 'Document Viewer';
    }
  }

  /** Generic captions callers pass as a title when no real file name is available. */
  private isPlaceholderTitle(value: string): boolean {
    return ['file viewer', 'document viewer', 'image viewer', 'video viewer', 'audio viewer', 'showpad document']
      .includes(value.trim().toLowerCase());
  }
}
